<template>
	<div>
		<v-dialog v-model="dialog" max-width="560" persistent>
			<v-card>
				<v-toolbar flat dense color="info" dark>
					<v-toolbar-title class="subheading">Update Policy (PDF)</v-toolbar-title>
					<v-spacer />
				</v-toolbar>

				<v-card-text>
					<!-- PDF info banner -->
					<v-alert :value="true" type="info" class="mb-4 text-xs-left" icon="mdi-file-upload" outline>
						<v-layout row align-center>
							<div>
								<div class="font-weight-medium">This policy is a PDF</div>
								<div class="caption">
									To update it, upload a new PDF file. You’ll choose whether to bump the version next.
								</div>
							</div>
						</v-layout>
					</v-alert>

					<!-- Name -->
					<v-text-field v-model="name" label="Policy Name" :rules="[v => !!v || 'Policy name is required']" :disabled="isSaving" required/>

					<v-card class="mt-3" flat tile>

						<input ref="file" type="file" accept="application/pdf" class="d-none" @change="onFileChange" :disabled="isSaving" />
							<v-btn :disabled="isSaving" @click="$refs.file && $refs.file.click()">
								<v-icon left>mdi-file-upload</v-icon>
								Choose File
							</v-btn>

							<div v-if="file" class="ml-3">
								<div class="body-2">{{ file.name }}</div>
								<div class="caption info-text">{{ prettySize(file.size) }}</div>
							</div>

						<div v-if="fileError" class="error--text v-messages mt-2">{{ fileError }}</div>
						<div class="info-text mt-2">
							This will replace the current PDF.
						</div>
					</v-card>

					<v-alert v-if="error" class="mt-3" :value="true" type="error" outline>
						{{ error }}
					</v-alert>
				</v-card-text>

				<v-card-actions>
					<v-spacer />
					<v-btn flat :disabled="isSaving" @click="onCancel">Cancel</v-btn>
					<v-btn flat color="primary" :loading="isSaving" :disabled="isSaving || !canSave" @click="onPrimarySave">
						Upload & Continue
						<span slot="loader">Saving...</span>
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<create-new-version-confirmation-dialog v-if="showConfirm" :dialog="showConfirm" :isConfirming="isSaving" @onConfirm="onConfirmVersion" @onCancel="showConfirm = false" />
	</div>
</template>

<script>
	import CreateNewVersionConfirmationDialog from '@/components/hrDocuments/CreateNewVersionConfirmationDialog';

	export default {
		name: 'ReplaceHRDocumentDialog',
		components: { CreateNewVersionConfirmationDialog },
		props: {
			dialog: { type: Boolean, required: true },
			isSaving: { type: Boolean, default: false },
			initialName: { type: String, default: '' },
			error: { type: String, default: null },
			confirmOnSave: { type: Boolean, default: true },
			maxFileMb: { type: Number, default: 20 }
		},
		data() {
			return {
				name: this.initialName,
				file: null,
				fileError: null,
				showConfirm: false,
				pendingFd: null
			};
		},
		watch: {
			initialName(v) {
				this.name = v;
			}
		},
		computed: {
			canSave() {
				return !!this.name && !!this.file && !this.fileError;
			}
		},
		methods: {
			isPdf(f) {
				return f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
			},
			isUnderLimit(f) {
				return f && f.size <= this.maxFileMb * 1024 * 1024;
			},
			prettySize(bytes) {
				if (!bytes && bytes !== 0) return '';
				const thresh = 1024;
				if (bytes < thresh) return bytes + ' B';
				const units = ['KB', 'MB', 'GB', 'TB'];
				let u = -1;
				do {
					bytes /= thresh;
					++u;
				} while (bytes >= thresh && u < units.length - 1);
				return bytes.toFixed(1) + ' ' + units[u];
			},
			onFileChange(e) {
				this.fileError = null;
				const f = e.target.files && e.target.files[0];
				if (!f) {
					this.file = null;
					return;
				}
				if (!this.isPdf(f)) {
					this.fileError = 'File must be a PDF (.pdf).';
					this.file = null;
					return;
				}
				if (!this.isUnderLimit(f)) {
					this.fileError = `File must be ${this.maxFileMb} MB or less.`;
					this.file = null;
					return;
				}
				this.file = f;
			},
			onPrimarySave() {
				// guard
				if (!this.canSave) {
					if (!this.file) this.fileError = 'PDF file is required.';
					return;
				}
				const fd = new FormData();
				fd.append('file', this.file);
				fd.append('name', this.name);

				if (this.confirmOnSave) {
					this.pendingFd = fd;
					this.showConfirm = true;
				} else {
					fd.append('incrementVersion', 'true');
					this.$emit('done', fd);
				}
			},
			onConfirmVersion(incrementVersion) {
				if (!this.pendingFd) return;
				this.pendingFd.set('incrementVersion', incrementVersion ? 'true' : 'false');
				this.$emit('done', this.pendingFd);
				this.pendingFd = null;
				this.showConfirm = false;
			},
			onCancel() {
				this.$emit('cancel');
			}
		}
	};
</script>

<style scoped>
	.info-text {
		color: rgba(0, 0, 0, 0.54);
	}
	.d-none {
		display: none;
	}
</style>
